import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStreamingContent } from '../api';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const StreamingSection = () => {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const data = await getStreamingContent();
        setContent(data);
      } catch (error) {
        console.error('Error fetching streaming content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  const handleWatch = (item) => {
    // Navigate to detail page instead of opening iframe directly
    navigate(`/streaming/${item.id}`);
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 3 >= content.length ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev === 0 ? Math.max(0, content.length - 3) : prev - 1));
  };

  if (loading) {
    return (
      <section id="streaming" className="w-full relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-blue-950 to-blue-950"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-950/30 to-black/40"></div>
        <div className="max-w-7xl mx-auto relative z-10 text-center text-white">
          <p className="text-2xl">Loading streaming content...</p>
        </div>
      </section>
    );
  }

  const visibleContent = content.slice(currentIndex, currentIndex + 3);
  const showArrows = content.length > 3;

  return (
    <section id="streaming" className="w-full relative py-20 px-6 overflow-hidden">
      {/* Gradient Background with Darker Shade */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-blue-950 to-blue-950"></div>
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-950/30 to-black/40"></div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="mb-12">
          {/* H2 - Title with lighter background */}
          <div className="bg-blue-800/50 backdrop-blur-sm rounded-2xl p-6 mb-4">
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              Streaming Now
            </h2>
          </div>
          
          {/* H3 + Content in darker background */}
          <div className="bg-blue-950/70 backdrop-blur-sm rounded-2xl p-6">
            <p className="text-lg text-gray-300 mb-6">Premiere streaming showcase</p>
            
            {content.length > 0 ? (
              <div className="relative">
                {/* Arrow Left */}
                {showArrows && currentIndex > 0 && (
                  <button
                    onClick={prevSlide}
                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-full p-3 transition-all duration-200"
                  >
                    <ChevronLeft size={32} />
                  </button>
                )}

                {/* Content Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {visibleContent.map((item) => (
                    <div
                      key={item.id}
                      className="group relative overflow-hidden rounded-lg shadow-2xl hover:scale-105 transition-transform duration-300 cursor-pointer"
                      onClick={() => handleWatch(item)}
                    >
                      <div className="relative aspect-[3/4] overflow-hidden">
                        <img
                          src={item.image}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                        <div className="absolute bottom-0 left-0 right-0 p-6">
                          {item.subtitle && (
                            <p className="text-white text-sm mb-2 font-semibold">
                              {item.subtitle}
                            </p>
                          )}
                          <h3 className="text-white text-2xl font-bold mb-3">
                            {item.title}
                          </h3>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleWatch(item);
                            }}
                            className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-full font-semibold transition-colors duration-200"
                          >
                            Tonton Sekarang
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Arrow Right */}
                {showArrows && currentIndex + 3 < content.length && (
                  <button
                    onClick={nextSlide}
                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-full p-3 transition-all duration-200"
                  >
                    <ChevronRight size={32} />
                  </button>
                )}
              </div>
            ) : (
              <div className="text-center text-white py-12">
                <p className="text-2xl">No streaming content available at the moment.</p>
                <p className="text-lg mt-2">Check back soon for updates!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default StreamingSection;